import React from "react";
import gql from "graphql-tag";
import { Link } from "@reach/router";
import { useQuery } from "@apollo/react-hooks";
import produce from "immer";
import { InView } from "react-intersection-observer";
import ReactPlaceholder from "react-placeholder";
import { RectShape } from "react-placeholder/lib/placeholders";
import { Container, Box, Image, Row, Column, Text, H3, Flex } from "@atoms";
import { useStoreAppValue } from "@hooks";
import { RailsUrl, renderIf, pluralize } from "@services";
import { CollectionRender, COLLECTION_TOP_SPACING } from "@organisms";
import { StoreFrontViewFragment } from "./__generated__/StoreFrontViewFragment";
import { LinkBlock } from "@organisms/linkBlockComponents";
import styled from "@emotion/styled";
import { theme } from "@styles";

// == Types ================================================================

interface IProps {
  storefront: StoreFrontViewFragment;
  isHome: boolean;
}

// == Constants ============================================================

const FRAGMENTS = {
  fields: gql`
    fragment StoreFrontViewFragment on StoreFront {
      id
      name
      headerImage
      collectionsPaginated(first: 5) {
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          node {
            id
            ...CollectionRenderFragment
          }
        }
      }
      contests {
        edges {
          cursor
          node {
            id
            current
            currency
            goal
            type
            url
          }
        }
      }
    }
    ${CollectionRender.fragments.fields}
  `,
};

const DEFAULT_PROPS = {};

// == Placeholder

const PlaceholderRow = () => (
  <Row mb={4}>
    <Column width={[1 / 2, 1 / 2, 1 / 4]}>
      <RectShape color="#E0E0E0" style={{ width: "100%", height: 150, marginRight: 0 }} />
    </Column>
    <Column width={[1 / 2, 1 / 2, 1 / 4]}>
      <RectShape color="#E0E0E0" style={{ width: "100%", height: 150, marginRight: 0 }} />
    </Column>
    <Column width={[1 / 2, 1 / 2, 1 / 4]}>
      <RectShape color="#E0E0E0" style={{ width: "100%", height: 150, marginRight: 0 }} />
    </Column>
    <Column width={[1 / 2, 1 / 2, 1 / 4]}>
      <RectShape color="#E0E0E0" style={{ width: "100%", height: 150, marginRight: 0 }} />
    </Column>
  </Row>
);

const numberWithCommas = (num) => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const CollectionPlaceholder = () => (
  <ReactPlaceholder
    showLoadingAnimation
    color="#E0E0E0"
    customPlaceholder={PlaceholderRow()}
    ready={false}
  />
);

// == More collections

const STORE_FRONT_COLLECTIONS_QUERY = gql`
  query StoreFrontCollections($id: ID!, $afterCursor: String, $carouselItems: Int = 10) {
    StoreFront(id: $id) {
      id
      collectionsPaginated(first: 5, after: $afterCursor) {
        pageInfo {
          endCursor
          hasNextPage
        }
        edges {
          cursor
          node {
            id
            ...CollectionRenderFragment
          }
        }
      }
    }
  }
  ${CollectionRender.fragments.fields}
`;

interface IStoreCollectionsProps {
  storeId: string;
  afterCursor: string;
}

const StoreCollections = ({ storeId, afterCursor, contests }: IStoreCollectionsProps) => {
  const { loading, data, error, fetchMore } = useQuery(STORE_FRONT_COLLECTIONS_QUERY, {
    variables: { id: storeId, afterCursor },
  });

  if (loading) {
    return (
      <Box mt={COLLECTION_TOP_SPACING}>
        <CollectionPlaceholder />
      </Box>
    );
  }
  if (error || !data) return null;

  const { collectionsPaginated } = data.StoreFront;

  return (
    <>
      <Box mb={COLLECTION_TOP_SPACING} mt={COLLECTION_TOP_SPACING}>
        {collectionsPaginated.edges.map((edge, index) => {
          return (
            <>
              <CollectionRender
                collection={edge.node}
                key={edge.node.id}
                showTopSpacing={index > 0}
              />
            </>
          );
        })}
      </Box>

      {collectionsPaginated.pageInfo.hasNextPage ? (
        <InView
          as="div"
          rootMargin="100px 0px 0px 0px"
          onChange={(inView: boolean) => {
            if (inView) {
              fetchMore({
                query: STORE_FRONT_COLLECTIONS_QUERY,
                variables: {
                  id: storeId,
                  afterCursor: collectionsPaginated.pageInfo.endCursor,
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                  return produce(previousResult, (draft) => {
                    draft.StoreFront.collectionsPaginated.pageInfo =
                      fetchMoreResult.StoreFront.collectionsPaginated.pageInfo;

                    draft.StoreFront.collectionsPaginated.edges = [
                      ...previousResult.StoreFront.collectionsPaginated.edges,
                      ...fetchMoreResult.StoreFront.collectionsPaginated.edges,
                    ];
                  });
                },
              });
            }
          }}
        >
          <CollectionPlaceholder />
        </InView>
      ) : null}
    </>
  );
};

const familyFeudBanner = (backgroundColor) => {
  return (
    <Box mt={1} mb={1}>
      <Row
        flexWrap="nowrap"
        style={{
          backgroundColor,
          marginLeft: "0px",
          marginRight: "0px",
          borderRadius: "4px",
          paddingTop: "5px",
          paddingBottom: "5px",
        }}
      >
        <Column autoColumn={false} width={1 / 6}>
          <Text style={{ color: "black" }}>#1</Text>
        </Column>
        <Column autoColumn={false} width={5 / 6}>
          <Text
            style={{
              color: "white",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
          >
            Golden Hawks
          </Text>
        </Column>
      </Row>
    </Box>
  );
};

// maybe needs viewer iuno
const ContestLeaderBoard = (activeCampaign) => {
  const rankings = 44;
  const totalTeams = 580123;
  return (
    // onclick will need to go somewhere
    <Box mb={COLLECTION_TOP_SPACING} mt={COLLECTION_TOP_SPACING}>
      <Row flexWrap="wrap">
        <Column autoColumn={false} pb={[3, 0]} width={[1, 1, 1 / 2]}>
          <Text>STATIC BANNER GOES HERE</Text>
        </Column>
        <Column autoColumn={false} pb={[3, 0]} width={[1, 1, 1 / 2]}>
          <Box style={{ position: "relative" }}>
            <Box>
              <Image
                src="https://flipgive.imgix.net/images/contests/confetti-background.png"
                style={{ width: "100%" }}
              />
            </Box>
            <Box
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                paddingTop: "2%",
              }}
            >
              <Box style={{ width: "100%", paddingLeft: "20px", paddingRight: "20px" }}>
                <Text
                  style={{ width: "100%", color: "black" }}
                  fontWeight="bold"
                  fontSize={["16px", "20px", "20px"]}
                >
                  Your team is <span style={{ color: theme.colors.primary }}>#{rankings}</span> of{" "}
                  {numberWithCommas(totalTeams)}.
                </Text>
              </Box>
              <Box style={{ width: "100%", paddingLeft: "20px", paddingRight: "20px" }}>
                <Text
                  style={{ width: "100%", color: "black" }}
                  fontWeight="bold"
                  fontSize={["16px", "20px", "20px"]}
                >
                  Here are the top teams:
                </Text>
              </Box>

              <Box style={{ padding: "0", margin: "0" }}>
                <Row style={{ padding: "0", margin: "0" }} flexWrap="wrap">
                  <Column
                    style={{ paddingLeft: "16px", paddingRight: "8px", margin: "0" }}
                    autoColumn={false}
                    pb={[3, 0]}
                    width={1 / 2}
                  >
                    {familyFeudBanner("#F8C35B")}
                    {familyFeudBanner("#BAC3C8")}
                    {familyFeudBanner("#CC875B")}
                    {familyFeudBanner(theme.colors.primary)}
                    {familyFeudBanner(theme.colors.primary)}
                  </Column>
                  <Column
                    style={{ paddingLeft: "8px", paddingRight: "16px", margin: "0" }}
                    autoColumn={false}
                    pb={[3, 0]}
                    width={1 / 2}
                  >
                    {familyFeudBanner(theme.colors.primary)}
                    {familyFeudBanner(theme.colors.primary)}
                    {familyFeudBanner(theme.colors.primary)}
                    {familyFeudBanner(theme.colors.primary)}
                    {familyFeudBanner(theme.colors.primary)}
                  </Column>
                </Row>
              </Box>
            </Box>
          </Box>
        </Column>
      </Row>
    </Box>
  );
};

const ContestRow = ({ contests }) => {
  const { current, goal, type, currency, url } = contests.edges[0].node;

  const lowerTest = current === null ? 0 : current;

  const maxCurrent = lowerTest > goal ? goal : current;
  const countryUrl =
    currency === "CAD"
      ? "https://flipgive.imgix.net/images/contests/RNR_RallyWidget_CAN2.png"
      : "https://flipgive.imgix.net/images/contests/RNR_RallyWidget_USA2.png";

  const remainingTeamMembersNeeded = goal - maxCurrent;

  return (
    // onclick will need to go somewhere
    <Box mb={COLLECTION_TOP_SPACING} mt={COLLECTION_TOP_SPACING}>
      <Row>
        <Column autoColumn={false} pb={3} width={1}>
          <Box
            style={{ cursor: "pointer" }}
            onClick={() =>
              (window.location.href = window.location.href =
                currency === "CAD"
                  ? "https://www.flipgive.com/pages/rally-and-raise-ca"
                  : "https://www.flipgive.com/pages/rally-and-raise-usa")
            }
          >
            {/* needs to get toggled to mobile on smaller bps */}
            <Box display={["block", "none", "none"]}>
              <Image
                src="https://flipgive.imgix.net/images/contests/1-desktop.png"
                style={{ width: "100%" }}
              />
            </Box>
            <Box display={["none", "block", "block"]}>
              <Image
                src="https://flipgive.imgix.net/images/contests/1-mobile.png"
                style={{ width: "100%" }}
              />
            </Box>
            <Image style={{ width: "100%" }} />
          </Box>
        </Column>
      </Row>

      <Row flexWrap="wrap">
        <Column autoColumn={false} pb={3} width={[1, 1, 1 / 2]}>
          <Box
            style={{ cursor: "pointer" }}
            onClick={() =>
              (window.location.href = window.location.href =
                currency === "CAD"
                  ? "https://www.flipgive.com/pages/rally-and-raise-ca"
                  : "https://www.flipgive.com/pages/rally-and-raise-usa")
            }
          >
            <Image src={countryUrl} style={{ width: "100%" }} />
          </Box>
        </Column>
        <Column autoColumn={false} pb={[3, 0]} width={[1, 1, 1 / 2]}>
          <Box
            style={{ position: "relative", cursor: "pointer" }}
            onClick={() => (window.location.href = url)}
          >
            <Box>
              <Image
                src="https://flipgive.imgix.net/images/contests/graffiti3.png"
                style={{ width: "100%" }}
              />
            </Box>
            <Box
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                right: 0,
                marginLeft: "auto",
                marginRight: "auto",
                width: "100%",
                paddingTop: "5%",
              }}
            >
              <Box style={{ width: "100%", paddingLeft: "20px", paddingRight: "20px" }}>
                <H3 style={{ width: "100%", marginBottom: "5px" }}>
                  Your team has <span style={{ color: theme.colors.primary }}>{current}</span> new{" "}
                  {pluralize("shopper", current, { showCount: false })}.{" "}
                </H3>
                <H3>
                  <span style={{ color: theme.colors.primary }}>{remainingTeamMembersNeeded}</span>{" "}
                  more to go!
                </H3>
              </Box>
              <Flex
                style={{
                  justifyContent: "center",
                  width: "100%",
                  position: "absolute",
                  bottom: "20%",
                }}
              >
                {[...Array(current)].map((member, index) => (
                  <Image
                    key={index}
                    style={{ width: "7%", marginLeft: "1%", marginRight: "1%" }}
                    src="https://flipgive.imgix.net/images/contests/teal_figure.png"
                  />
                ))}
                {[...Array(remainingTeamMembersNeeded)].map((member, index) => (
                  <Image
                    key={index}
                    style={{ width: "7%", marginLeft: "1%", marginRight: "1%" }}
                    src="https://flipgive.imgix.net/images/contests/black_figure.png"
                  />
                ))}
              </Flex>
            </Box>
          </Box>
        </Column>
      </Row>
      <Row flexWrap="wrap">
        <Column autoColumn={false} pb={3} width={[1, 1, 1 / 2]}>
          <Box
            style={{ cursor: "pointer" }}
            onClick={() =>
              (window.location.href =
                currency === "CAD"
                  ? "https://www.flipgive.com/pages/rally-and-raise-ca"
                  : "https://www.flipgive.com/pages/rally-and-raise-usa")
            }
          >
            <Image
              src={
                currency === "CAD"
                  ? "https://flipgive.imgix.net/images/contests/3A-CAN.png"
                  : "https://flipgive.imgix.net/images/contests/3A-USA.png"
              }
              style={{ width: "100%" }}
            />
          </Box>
        </Column>
        <Column autoColumn={false} pb={3} width={[1, 1, 1 / 2]}>
          <Box
            style={{ cursor: "pointer" }}
            onClick={() =>
              (window.location.href =
                currency === "CAD"
                  ? "https://www.flipgive.com/pages/rally-and-raise-ca"
                  : "https://www.flipgive.com/pages/rally-and-raise-usa")
            }
          >
            <Image
              src={
                currency === "CAD"
                  ? "https://flipgive.imgix.net/images/contests/3B-CAN.png"
                  : "https://flipgive.imgix.net/images/contests/3B-USA.png"
              }
              style={{ width: "100%" }}
            />
          </Box>
        </Column>
      </Row>
    </Box>
  );
};

// == Component ============================================================

const StoreFrontView = ({ storefront, isHome }: IProps) => {
  const { collectionsPaginated, name, id, contests } = storefront;

  return (
    <Container isMainContainer pt={0}>
      {!!storefront.headerImage && (
        <Box mb={COLLECTION_TOP_SPACING}>
          <Image alt={`${storefront.name}-content-item`} src={storefront.headerImage} />
        </Box>
      )}

      {/* We're gonna put the new contest here as #1 and pretend were getting data here*/}
      {/* <ContestLeaderBoard /> */}
      {id && id === "63" && <ContestRow contests={contests} />}

      {collectionsPaginated?.edges?.map((edge, index) => (
        <CollectionRender collection={edge.node} key={edge.node.id} showTopSpacing={index > 0} />
      ))}

      {collectionsPaginated.pageInfo.hasNextPage ? (
        <StoreCollections
          afterCursor={collectionsPaginated.pageInfo.endCursor}
          storeId={id}
          contests={contests}
        />
      ) : null}
    </Container>
  );
};

StoreFrontView.fragments = FRAGMENTS;
StoreFrontView.defaultProps = DEFAULT_PROPS;

export default StoreFrontView;

// == Styles ===============================================================
